import { ref, createApp } from "vue";
import NotificationToast from "@/components/ui/NotificationToast.vue";
import type { Notification } from "@/types";

const notifications = ref<Notification[]>([]);
let nextId = 1;

// Stocker les fonctions de nettoyage des notifications actives
const activeCleanups: Array<() => void> = [];

export function useNotification() {
    function show(type: "success" | "error" | "warning" | "modifier", message: string, duration = 3000) {
        // Fermer toutes les notifications existantes avant d'en afficher une nouvelle
        while (activeCleanups.length > 0) {
            const cleanup = activeCleanups.pop();
            if (cleanup) cleanup();
        }
        notifications.value = [];

        const id = nextId++;
        const notification: Notification = { id, type, message, duration };

        notifications.value.push(notification);

        // Créer et monter le composant de notification
        const container = document.createElement("div");
        document.body.appendChild(container);

        const cleanup = () => {
            try {
                app.unmount();
            } catch (e) { /* already unmounted */ }
            if (container.parentNode) {
                document.body.removeChild(container);
            }
        };

        const app = createApp(NotificationToast, {
            type: notification.type,
            message: notification.message,
            duration: notification.duration,
            offsetIndex: 0,
            onClose: () => {
                cleanup();
                const index = notifications.value.findIndex((n) => n.id === id);
                if (index > -1) {
                    notifications.value.splice(index, 1);
                }
                const ci = activeCleanups.indexOf(cleanup);
                if (ci > -1) activeCleanups.splice(ci, 1);
            },
        });

        activeCleanups.push(cleanup);
        app.mount(container);
    }

    function success(message: string, duration?: number) {
        show("success", message, duration);
    }

    function error(message: string, duration?: number) {
        show("error", message, duration);
    }

    function warning(message: string, duration?: number) {
        show("warning", message, duration);
    }

    function modifier(message: string, duration?: number) {
        show("modifier", message, duration);
    }

    return {
        notifications,
        show,
        success,
        error,
        warning,
        modifier,
    };
}
